import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";
import { CircleNotch } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { useEffect, useRef, useState } from "react";

import { RobotIcon } from "./ChatMessage";

interface ThinkingStateProps {
  /** undefined = no assistant message yet (phase 0) */
  message?: AskPunttMessage;
}

function getPhase(message?: AskPunttMessage): {
  phase: number;
  text: string;
} {
  if (!message) {
    return { phase: 0, text: "Thinking" };
  }

  const hasSteps = (message.agentSteps?.length ?? 0) > 0;
  const hasSearching = message.searchProgress?.some(
    (s) => s.status === "searching",
  );
  const hasStreamingContent = !!message.streamingContent;

  if (hasStreamingContent) {
    return { phase: 4, text: "Writing response..." };
  }

  if (hasSteps) {
    return { phase: 3, text: "Analyzing..." };
  }

  if (hasSearching) {
    const searchingSource = message.searchProgress?.find(
      (s) => s.status === "searching",
    );
    return {
      phase: 2,
      text: `Researching ${searchingSource?.source ?? ""}...`.trim(),
    };
  }

  return { phase: 1, text: "Starting up..." };
}

export function ThinkingState({ message }: ThinkingStateProps) {
  const { phase, text } = getPhase(message);
  const [displayText, setDisplayText] = useState(text);
  const [fading, setFading] = useState(false);
  const prevTextRef = useRef(text);

  useEffect(() => {
    if (text !== prevTextRef.current) {
      setFading(true);
      const timer = setTimeout(() => {
        setDisplayText(text);
        setFading(false);
        prevTextRef.current = text;
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [text]);

  // Phase 0: Original "Thinking" with bouncing dots
  if (phase === 0) {
    return (
      <div
        data-testid="ask-puntt-thinking-state"
        className="flex items-center gap-3 rounded-2xl border border-puntt-neutral-gray-4 bg-base-white p-4 shadow-sm"
      >
        <div className="animate-thinking-pulse motion-reduce:animate-none">
          <RobotIcon />
        </div>
        <div className="flex items-baseline gap-1.5">
          <Text size="2" weight="medium" className="text-puntt-neutral-gray-11">
            Thinking
          </Text>
          <span className="flex gap-0.5 motion-reduce:hidden">
            <span className="size-1.5 animate-bounce rounded-full bg-puntt-accent-9 [animation-delay:0ms]" />
            <span className="size-1.5 animate-bounce rounded-full bg-puntt-accent-9 [animation-delay:150ms]" />
            <span className="size-1.5 animate-bounce rounded-full bg-puntt-accent-9 [animation-delay:300ms]" />
          </span>
        </div>
      </div>
    );
  }

  // Phases 1-4: Spinner + phase text + phase dots
  return (
    <div
      data-testid="ask-puntt-thinking-state"
      className="flex items-center gap-2"
    >
      <CircleNotch
        size={14}
        className="animate-spin text-puntt-accent-9 motion-reduce:animate-none"
      />
      <Text
        size="2"
        className={`text-puntt-neutral-gray-9 transition-opacity duration-150 ${fading ? "opacity-0" : "opacity-100"}`}
      >
        {displayText}
      </Text>
      <span className="ml-1 flex gap-1">
        {[1, 2, 3, 4].map((dot) => (
          <span
            key={dot}
            className={`size-1.5 rounded-full transition-colors duration-200 ${
              dot <= phase ? "bg-puntt-accent-9" : "bg-puntt-neutral-gray-5"
            }`}
          />
        ))}
      </span>
    </div>
  );
}
